import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import renderModal from '@components/show_page/modals/renderModal';
import { get as getLabels } from '@api/v4/resources/labels';
import { get as getSections } from '@api/v4/brandfolders/sections';
import { get as getCollections } from '@api/v4/brandfolders/collections';
import { create as createRule } from '@api/v4/resources/rules';
import { update as updateRule } from '@api/v4/rules';
import RuleSelection from './1_rule_selection';
import TriggerSelection from './2_trigger_selection';
import ActionSelection from './3_action_selection';
import Watermark from './4_watermark';
import Summary from './5_summary';
import './styles/main.scss';

function RuleModal(props) {
  const { closeModal, ruleData } = props;

  const [stage, updateStage] = useState(ruleData ? 2 : 1);
  const [ruleType, updateRuleType] = useState(ruleData ? `Rule::${ruleData.type}${!ruleData.type.includes("Watermark") ? "Association" : ""}` : undefined);
  const [scope, updateScope] = useState(ruleData?.scope);
  const [actionableKeys, updateActionableKeys] = useState([]);
  const [actionableResources, updateActionableResources] = useState([]);
  const [enforce, updateEnforce] = useState(false);
  const [allSelected, updateAllSelected] = useState(false);

  const resetState = () => {
    updateActionableResources([]);
    updateActionableKeys([]);
    if (!ruleData) {
      updateStage(1);
      updateScope(null);
    }
    updateEnforce(false);
    updateAllSelected(false);
  };

  const setActionableResources = (type) => {
    if (type.includes("CollectionAssociation")) {
      resetState();
      getCollections({ fields: "parent_id" }).then((resources) => {
        updateActionableResources(resources);
      });
    }
    if (type.includes("LabelAssociation")) {
      resetState();
      getLabels(true /* fetchAll */).then((resources) => {
        updateActionableResources(resources);
      });
    }
    if (type.includes("SectionAssociation")) {
      resetState();
      getSections().then((resources) => {
        updateActionableResources(resources);
      });
    }
    // Rule::Tag::Watermark
    resetState();
  };

  useEffect(() => {
    if (ruleData) {
      setActionableResources(`Rule::${ruleData.type}${!ruleData.type.includes("Watermark") ? "Association" : ""}`);
      updateActionableKeys(ruleData.actionable_keys);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleData]);

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      updateActionableKeys([...actionableResources.map((resource) => resource.id)]);
    } else {
      updateActionableKeys([]);
    }
    updateAllSelected(isSelected);
  };

  const submitNewRule = () => {
    createRule({ type: ruleType, actionable_keys: actionableKeys, scope, enforce }).then(() => {
      closeModal();
      let title = bfTranslate("Rule Enabled");
      if (BFG.hasFeature('watermarking') && ruleType.includes("Watermark")) {
        title = bfTranslate("Rule enabled. Watermark will only be applied to supported file types: JPG, JPEG, and PNG.");
      }
      Notify.create({
        title,
        type: "success"
      });
      document.querySelectorAll("a.nav-link[href='rules']")[0].click();
    })
      .catch(({ errors }) => {
        Notify.create({
          title: bfTranslate("Invalid Rule"),
          body: errors,
          type: "error"
        });
        closeModal();
      });
  };

  const submitUpdatedRule = () => {
    updateRule({ ruleKey: ruleData.key, actionableKeys, scope }).then(() => {
      closeModal();
      let title = bfTranslate("Rule Updated");
      if (BFG.hasFeature('watermarking') && ruleData.type.includes("Watermark")) {
        title = bfTranslate("Rule updated. Watermark will only be applied to supported file types: JPG, JPEG, and PNG.");
      }
      Notify.create({
        title,
        type: "success"
      });
      document.querySelectorAll("a.nav-link[href='rules']")[0].click();
    })
      .catch(({ errors }) => {
        Notify.create({
          title: bfTranslate("Invalid Rule"),
          body: errors,
          type: "error"
        });
        closeModal();
      });
  };

  const renderStage = () => {
    switch (stage) {
      case 2:
        return (
          <TriggerSelection
            ruleType={ruleType}
            scope={scope}
            updateScope={updateScope}
          />
        );
      case 3:
        return (
          <ActionSelection
            actionableKeys={actionableKeys}
            actionableResources={actionableResources}
            allSelected={allSelected}
            handleSelectAll={handleSelectAll}
            ruleData={ruleData}
            ruleType={ruleType}
            updateActionableKeys={updateActionableKeys}
          />
        );
      case 4:
        return (
          <Watermark
            scope={scope}
            updateScope={updateScope}
          />
        );
      case 5:
        return (
          <Summary
            actionableKeys={actionableKeys}
            actionableResources={actionableResources}
            enforce={enforce}
            ruleType={ruleType}
            scope={scope}
            updateEnforce={updateEnforce}
          />
        );
      default:
        return (
          <RuleSelection
            resetState={resetState}
            ruleType={ruleType}
            setActionableResources={setActionableResources}
            updateRuleType={updateRuleType}
          />
        );
    }
  };

  const stageTitle = () => {
    switch (stage) {
      case 2:
        return bfTranslate("Select Trigger");
      case 3:
        return bfTranslate("Select Action");
      case 4:
        return bfTranslate("Add Watermark");
      case 5:
        return bfTranslate("Summary");
      default:
        return bfTranslate("Add Rule");
    }
  };

  const disableButton = () => {
    if (stage === 1 && !ruleType) {
      return true;
    }
    if (stage === 2 && !scope) {
      return true;
    }
    if (stage === 3 && !actionableKeys.length) {
      return true;
    }
    if (stage === 4 && (!scope || (scope && !scope.watermark_url))) {
      return true;
    }
    return false;
  };

  const incrementStage = () => {
    if (stage === 2 && ruleType.includes("Watermark")) {
      // Select Trigger => Add Watermark (skip over Select Action)
      updateStage(4);
    } else if (stage === 3 && !ruleType.includes("Watermark")) {
      // Select Action => Summary (skip over Add Watermark)
      updateStage(5);
    } else {
      updateStage(stage + 1);
    }
  };

  const handleStageUpdate = () => {
    if (stage < 5) {
      incrementStage();
    } else if (ruleData) {
      submitUpdatedRule();
    } else {
      submitNewRule();
    }
  };

  let buttonText = bfTranslate('Next');
  if (stage === 5) {
    buttonText = ruleData ? bfTranslate('Update Rule') : bfTranslate('Create Rule');
  }

  return (
    <div className="modal-content-wrapper add-labels-modal">
      <div className="modal-content-wrapper__header">
        <span aria-hidden="true" className="bff-rules-engine icon" />
        <h3 className="modal-title">{stageTitle()}</h3>
      </div>
      <div className="modal-content-wrapper__body">
        { renderStage() }
        <div className="button-container">
          <button
            className="button tertiary sm t-close-modal"
            onClick={() => closeModal()}
            type="button"
          >
            {bfTranslate('Cancel')}
          </button>
          <button
            className="button sm primary t-confirm-modal"
            disabled={disableButton()}
            onClick={handleStageUpdate}
            type="button"
          >

            { buttonText }
          </button>
        </div>
      </div>
    </div>
  );
}

RuleModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  ruleData: PropTypes.shape({})
};

RuleModal.defaultProps = {
  ruleData: false
};

const ModalComponent = renderModal(RuleModal, 'RuleModal');
export default ModalComponent;
